import { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * RedirectPage Component
 *
 * A reusable component to redirect from React app to Next.js app or external URLs
 * Uses window.location for cross-application navigation
 *
 * @param {string} to - The destination path to redirect to (can be relative or absolute URL)
 * @param {boolean} replace - Whether to replace the current history entry (default: true)
 * @param {number} delay - Delay in milliseconds before redirecting (default: 0)
 * @param {boolean} external - Whether the URL is external (default: false)
 *
 * @example
 * // Redirect from React app to Next.js app:
 * <Route path="/ad-campaigns" element={<RedirectPage to="/v2/ad-campaigns" />} />
 *
 * @example
 * // With custom delay:
 * <Route path="/old-page" element={<RedirectPage to="/v2/new-page" delay={1000} />} />
 *
 * @example
 * // External URL:
 * <Route path="/youtube" element={<RedirectPage to="https://youtube.com" external />} />
 */
const RedirectPage = ({ to, replace = true, delay = 0, external = false }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (replace) {
        window.location.replace(to);
      } else {
        window.location.href = to;
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [to, replace, delay]);

  // Optional: Show a loading message during redirect
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '16px',
      color: '#666'
    }}>
      Redirecting...
    </div>
  );
};

RedirectPage.propTypes = {
  to: PropTypes.string.isRequired,
  replace: PropTypes.bool,
  delay: PropTypes.number,
  external: PropTypes.bool
};

export default RedirectPage;
